import { lazy, Suspense, useEffect, useRef, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { hideSplash, isNative, notifyAppReady, openInAppBrowser, WEBSITE_ORIGIN } from "@/lib/platform";
import { appRechtsSeite, isAllowedNativePath, istAppTeamPfad, NATIVE_HOME } from "@/lib/nativeRoutes";

// Titelzeile mit Zurück-Knopf über den Rechtsseiten, nur in der App nachgeladen (Web-Bundle gleich).
const AppRechtsKopf = lazy(() => import("@/components/app/AppRechtsKopf"));
import { useTeamZustand } from "@/lib/teamModus";

// Im Web ein reiner Durchreicher. In der App:
// - "/" führt in den Kundenbereich.
// - Freigegebene Routen rendern normal (Query und Hash bleiben, z. B. /auth?next=...).
// - Jede andere Route (Marketing, Schnuppertag, Blog ...) wird NICHT in der App gerendert,
//   sondern auf charliescrew.com im In-App-Browser geöffnet; die App bleibt auf der
//   Seite, von der der Link kam.
// - Team-Seiten (OA-5): nur für Personen mit Team-Recht hinter dem Schalter `team_seiten`
//   (Liste APP_TEAM_PFADE, Rechte aus page_access). Solange die Rechte laden, wartet eine
//   Team-Adresse leer; ohne Recht geht sie still in den Kundenbereich (nie In-App-Browser).
//   /admin (Brain) gibt es in der App nicht: mit Team-Recht führt es auf die Team-Startseite.
// - Rechtsseiten (Impressum, Datenschutz, AGB, ...) bekommen oben eine Titelzeile mit Zurück
//   (AppRechtsKopf), sonst wären sie ohne Wischen-zurück eine Sackgasse (Bug 16.10.2026).
export function NativeRouteGuard({ children }: { children: ReactNode }) {
  const native = isNative();
  const { pathname, search, hash, key } = useLocation();
  const navigate = useNavigate();
  const team = useTeamZustand();
  const teamAdresse = native && istAppTeamPfad(pathname);
  const brainAdresse = native && (pathname === "/admin" || pathname === "/admin/");
  const teamWartet = (teamAdresse || brainAdresse) && team.status === "offen";
  const teamUmleitung: string | null = !native || teamWartet
    ? null
    : brainAdresse && team.status === "ja" && team.start
      ? team.start
      : teamAdresse && team.status === "nein"
        ? NATIVE_HOME
        : null;
  const allowed = !native || teamWartet || teamUmleitung !== null
    || isAllowedNativePath(pathname, team.status === "ja" ? team.pfade : undefined);
  const isRoot = native && pathname === "/";
  const lastOpened = useRef<string | null>(null);

  // Sobald React steht: Splash ausblenden (Fallback: nativer Auto-Hide in capacitor.config)
  // und Capgo bestätigen, dass dieses Bundle läuft (sonst automatischer Rollback).
  useEffect(() => {
    if (!native) return;
    void hideSplash();
    void notifyAppReady();
  }, [native]);

  // Links aus Mails (Universal Links / App Links) und Android-Zurück-Taste. Nur in der App
  // nachgeladen, damit das Web-Start-Bundle gleich bleibt.
  const pfadRef = useRef(`${pathname}${search}${hash}`);
  pfadRef.current = `${pathname}${search}${hash}`;
  // navigate wechselt in react-router v6 mit jedem Pfad; Listener sollen nur einmal starten.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  useEffect(() => {
    if (!native) return;
    let aufraeumen: (() => void) | null = null;
    let aktiv = true;
    void import("@/lib/nativeEvents").then(({ nativeEventsStarten }) =>
      nativeEventsStarten({ navigate: (pfad) => navigateRef.current(pfad), aktuellerPfad: () => pfadRef.current }),
    ).then((fn) => {
      if (aktiv) aufraeumen = fn;
      else fn();
    }).catch(() => { /* App läuft auch ohne */ });
    return () => {
      aktiv = false;
      aufraeumen?.();
    };
  }, [native]);

  // Eigene Links mit target="_blank" (z. B. AGB im Bedingungs-Dialog): iOS reicht sie als
  // capacitor://localhost/… an das System weiter, und das öffnet eine fremde App, die dasselbe
  // Schema registriert hat (Tester-Feedback 19.10.2026). Darum hier abfangen und die Seite auf
  // charliescrew.com im In-App-Browser zeigen; die App bleibt, wo sie war (Dialog bleibt offen).
  useEffect(() => {
    if (!native) return;
    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.("a[target=_blank]") as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute("href") ?? "";
      let ziel: URL;
      try { ziel = new URL(href, window.location.href); } catch { return; }
      if (ziel.origin !== window.location.origin) return;
      e.preventDefault();
      void openInAppBrowser(`${WEBSITE_ORIGIN}${ziel.pathname}${ziel.search}${ziel.hash}`);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [native]);

  useEffect(() => {
    if (allowed || isRoot) {
      // Zurück auf einer erlaubten Seite: derselbe Link darf beim nächsten Tippen wieder öffnen.
      lastOpened.current = null;
      return;
    }
    const target = `${pathname}${search}${hash}`;
    // StrictMode/Doppel-Render: dieselbe Seite nicht zweimal öffnen.
    if (lastOpened.current !== target) {
      lastOpened.current = target;
      void openInAppBrowser(`${WEBSITE_ORIGIN}${target}`);
    }
    // Kam der Aufruf aus der App (Link/navigate), zurück auf die vorige Seite. War die
    // gesperrte Route der erste Eintrag dieses Dokuments (Deep Link, Rückkehr von Stripe),
    // gibt es kein "zurück" innerhalb der App → Kundenbereich. key === "default" heißt genau
    // das; window.history.length taugt nicht, weil es auch fremde Einträge zählt.
    if (key !== "default") navigate(-1);
    else navigate(NATIVE_HOME, { replace: true });
  }, [allowed, isRoot, pathname, search, hash, key, navigate]);

  if (isRoot) return <Navigate to={NATIVE_HOME} replace />;
  if (teamUmleitung) return <Navigate to={teamUmleitung} replace />;
  if (teamWartet) return null;
  if (!allowed) return null;
  const rechtsSeite = native ? appRechtsSeite(pathname) : null;
  // Immer dieselbe Baumform, damit children beim Wechsel auf/von einer Rechtsseite nicht neu mounten.
  return (
    <>
      {rechtsSeite && (
        // Fallback hält schon beim Nachladen den Platz der Titelzeile frei (kein Sprung).
        <Suspense fallback={rechtsSeite.eigeneKopfzeile ? null : <div aria-hidden="true" style={{ height: 44 }} />}>
          <AppRechtsKopf />
        </Suspense>
      )}
      {children}
    </>
  );
}
